import * as React from 'react';
import { DefaultButton, IconButton, Panel, PanelType, Spinner, SpinnerSize } from '@fluentui/react';

import { useNotifications, NOTIFICATION_POLL_INTERVAL_MS } from './useNotifications';
import { notificationBellStyles as styles } from './NotificationBell.styled';
import type { INotification } from '../../../Interfaces/INotification';

interface INotificationBellProps {
  pollIntervalMs?: number;
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString();
}

export const NotificationBell: React.FC<INotificationBellProps> = ({
  pollIntervalMs = NOTIFICATION_POLL_INTERVAL_MS,
}) => {
  const { notifications, unreadCount, isLoading, markRead, markAllRead, refresh } = useNotifications(pollIntervalMs);
  const [isOpen, setIsOpen] = React.useState(false);

  // The panel is an unread inbox: only surface notifications the user has not read yet.
  // Read items (either marked individually, via "Mark all as read", or already read from a
  // previous session) must not reappear on click or after a reload/re-fetch.
  const visibleNotifications = React.useMemo(() => notifications.filter((n) => !n.isRead), [notifications]);

  const openPanel = React.useCallback(() => {
    setIsOpen(true);
    refresh();
  }, [refresh]);

  const dismissPanel = React.useCallback(() => setIsOpen(false), []);

  const handleItemClick = React.useCallback(
    (notification: INotification) => {
      if (!notification.isRead) {
        markRead(notification.notificationId);
      }
    },
    [markRead]
  );

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const bellAriaLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <span className={styles.bellWrapper}>
      <IconButton
        iconProps={{ iconName: 'Ringer' }}
        title="Notifications"
        ariaLabel={bellAriaLabel}
        onClick={openPanel}
        styles={{
          root: {
            width: 48,
            height: 48,
            padding: 0,
          },
          icon: { color: '#ffffff', fontSize: 18 },
          iconHovered: { color: '#ffffff' },
          iconPressed: { color: '#ffffff' },
          rootHovered: { backgroundColor: 'rgba(255,255,255,0.15)' },
          rootPressed: { backgroundColor: 'rgba(255,255,255,0.25)' },
        }}
      />
      {unreadCount > 0 && (
        <span className={styles.badge} aria-hidden="true" data-testid="notification-badge">
          {badgeLabel}
        </span>
      )}

      <Panel
        isOpen={isOpen}
        onDismiss={dismissPanel}
        type={PanelType.medium}
        headerText="Notifications"
        closeButtonAriaLabel="Close"
        isBlocking={false}
      >
        <div className={styles.panelActions}>
          <DefaultButton text="Mark all as read" onClick={markAllRead} disabled={unreadCount === 0} />
        </div>

        {isLoading && visibleNotifications.length === 0 ? (
          <Spinner size={SpinnerSize.large} label="Loading notifications..." />
        ) : visibleNotifications.length === 0 ? (
          <div className={styles.emptyState}>You have no notifications.</div>
        ) : (
          <div className={styles.panelBody}>
            {visibleNotifications.map((n) => (
              <div
                key={n.notificationId}
                className={`${styles.item} ${n.isRead ? '' : styles.itemUnread}`}
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(n)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleItemClick(n);
                  }
                }}
              >
                <span className={styles.itemMessage}>{n.message}</span>
                <span className={styles.itemMeta}>{formatTimestamp(n.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </span>
  );
};
